import React, { useEffect, useState, useMemo } from "react";
import {
  FaFilter,
  FaSearch,
  FaChevronRight,
  FaChevronLeft,
} from "react-icons/fa";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from "react-accessible-accordion";
import Switch from "react-switch";
import PickDate from "../../components/common/datePicker";
import Footer from "../../components/footer";
import { useAuth } from "../../services/authProvider";
import moment from "moment";
import { formatUtcTimestamp } from "../../utils/helper";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import GetTeamScoreSummary from "../../hooks/getTeamScoreSummary";

const FunctionalSummariesTeamScore = () => {
  // API data state
  const [summariesData, setSummariesData] = useState([]);

  // Filter states
  const [authorFilter, setAuthorFilter] = useState(null);
  const [positionFilter, setPositionFilter] = useState(null);
  const [dateRangeFilter, setDateRangeFilter] = useState(null);
  const [authorIcon, setAuthorIcon] = useState(false);
  const [positionIcon, setPositionIcon] = useState(false);
  const [dateRangeIcon, setDateRangeIcon] = useState(false);

  // Filter modal states
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [filterAuthors, setFilterAuthors] = useState(null);
  const [availableAuthors, setAvailableAuthors] = useState([]);

  // Switch states
  const [checkedAuthors, setCheckedAuthors] = useState({
    check: false,
    attr: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Icon rotation states
  const authorIconRotate = authorIcon ? "rotate(180deg)" : "rotate(0)";
  const positionIconRotate = positionIcon ? "rotate(180deg)" : "rotate(0)";
  const dateRangeIconRotate = dateRangeIcon ? "rotate(180deg)" : "rotate(0)";

  // Filter options - dynamically generated from API data
  const getPositions = useMemo(() => {
    if (!summariesData || !Array.isArray(summariesData)) return [];
    const positions = [
      ...new Set(summariesData.map((item) => item.position).filter(Boolean)),
    ];
    return positions.map((position) => ({ label: position, value: position }));
  }, [summariesData]);

  const getDateRanges = [
    { label: "Last 7 days", value: "7days" },
    { label: "Last 30 days", value: "30days" },
    { label: "Last 3 months", value: "3months" },
    { label: "Last 6 months", value: "6months" },
  ];

  const {
    data,
    isLoading: teamScoreLoading,
    error,
    refetch,
  } = GetTeamScoreSummary(
    localStorage.getItem("patient"),
    filterStartDate,
    filterEndDate,
    filterAuthors
  );

  // Transform API data to match component expectations
  useEffect(() => {
    if (data && data.data && Array.isArray(data.data)) {
      const transformedData = data.data.map((item) => ({
        id: item.id,
        dateCreated: item.dateCreated,
        fromDate: item.fromDate,
        toDate: item.toDate,
        author: item.provider
          ? `${item.provider?.firstName || ""} ${
              item.provider?.lastName || ""
            }`.trim()
          : "Unknown",
        authorId: item.provider?.id || null, // Store the provider ID
        position: item.provider?.position?.name || "Unknown",
        status: item.status,
        active: item.active,
      }));
      setSummariesData(transformedData);

      // Populate available authors for filter with both ID and name
      const authorsMap = new Map();
      transformedData
        .filter((item) => item.authorId && item.author !== "Unknown")
        .forEach((item) => {
          // Use Map to ensure uniqueness by ID
          if (!authorsMap.has(item.authorId)) {
            authorsMap.set(item.authorId, {
              id: item.authorId,
              name: item.author
            });
          }
        });
      
      const authors = Array.from(authorsMap.values());
      setAvailableAuthors(authors);
     
      
    }
  }, [data]);

  // Update loading state to use API loading
  useEffect(() => {
    setIsLoading(teamScoreLoading);
  }, [teamScoreLoading]);

  // Handle error state
  useEffect(() => {
    if (error) {
      console.error("Error fetching team score summary:", error);
    }
  }, [error]);
  // Filter handlers
  const handleAuthor = (selected) => {
    setAuthorFilter(selected);
  };

  const handlePosition = (selected) => {
    setPositionFilter(selected);
  };

  const handleDateRange = (selected) => {
    setDateRangeFilter(selected);
    setDateRangeIcon(false);
  };

  // Filter modal functions


  const formatDateForAPI = (date) => {
    return date ? moment(date).format("YYYY-MM-DD") : null;
  };

  const handleChecked = (status, e, id) => {
    if (id === "authors") {
      setCheckedAuthors({
        check: status,
        attr: id,
      });
      if (!status) {
        setSelectedAuthors([]);
        setFilterAuthors(null);
      }
    }
  };

  const handleAuthorSelection = (authorId, isChecked) => {
    if (isChecked) {
      setSelectedAuthors([...selectedAuthors, authorId]);
    } else {
      setSelectedAuthors(selectedAuthors.filter((id) => id !== authorId));
    }
  };

  const handleSearch = () => {
    const formattedStartDate = formatDateForAPI(startDate);
    const formattedEndDate = formatDateForAPI(endDate);
    
    
    setFilterStartDate(formattedStartDate);
    setFilterEndDate(formattedEndDate);
    setFilterAuthors(selectedAuthors.length > 0 ? selectedAuthors : null);
    setCurrentPage(1);
    // Trigger refetch with new filter parameters
    setTimeout(() => refetch(), 100);
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setSelectedAuthors([]);
    setFilterAuthors(null);
    setCheckedAuthors({
      check: false,
      attr: "",
    });
    setCurrentPage(1);
    // Trigger refetch with cleared filter parameters
    setTimeout(() => refetch(), 100);
  };

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  };

  const clearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
  };

  // Filter data based on search term only (date and author filtering now done server-side)
  const getFilteredData = () => {
    let baseData = summariesData || [];





    // Apply search term filter
    if (!searchTerm.trim()) {
      return baseData;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    const filtered = baseData.filter((item) => {
      // Search in author name
      const authorMatch = item.author?.toLowerCase().includes(searchLower);

      // Search in position
      const positionMatch = item.position?.toLowerCase().includes(searchLower);

      // Search in date created
      const dateCreatedMatch = formatUtcTimestamp(item.dateCreated)
        .toLowerCase()
        .includes(searchLower);

      // Search in date range
      const fromDateMatch =
        item.fromDate && moment(item.fromDate).isValid()
          ? moment(item.fromDate)
              .format("D MMM YYYY")
              .toLowerCase()
              .includes(searchLower)
          : false;

      const toDateMatch =
        item.toDate && moment(item.toDate).isValid()
          ? moment(item.toDate)
              .format("D MMM YYYY")
              .toLowerCase()
              .includes(searchLower)
          : false;

      // Search in status
      const statusMatch = item.status?.toLowerCase().includes(searchLower);

      return (
        authorMatch ||
        positionMatch ||
        dateCreatedMatch ||
        fromDateMatch ||
        toDateMatch ||
        statusMatch
      );
    });

    return filtered;
  };

  // Pagination logic
  const filteredData = getFilteredData();
  const totalElements = filteredData.length;
  const totalPages = Math.ceil(totalElements / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = filteredData.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page
  };

  const hasNextPage = currentPage < totalPages;
  const hasPreviousPage = currentPage > 1;


  return (
    <>
      <div className="container-fluid py-3 px-md-4 px-2 h-100 noScroll">
        <div className="card border-0 h-100">
          <div className="card-body px-md-3 px-2 mb-3">
            <h5 className="font-weight-lighter">
              HCP: VIEW Functional Summaries with Team Consensus Scores
            </h5>
            <hr />

            <div className="d-flex justify-content-between align-items-start flex-wrap mobile-stack">
              <div className="mb-2 mb-md-0">
                <h5 className="font-weight-lighter mt-2">
                  {(searchTerm.trim() || filterStartDate || filterEndDate || (filterAuthors && filterAuthors.length > 0)) && (
                    <>
                      <br />
                      <small className="text_primary">
                        Time Filtered: {moment(filterStartDate).format("D MMM YYYY")} to {moment(filterEndDate).format("D MMM YYYY")}
                      </small>
                    </>
                  )}
                </h5>
              </div>
              <div
                className="d-flex align-items-center mobile-full-width"
                style={{ maxWidth: "500px" }}
              >
                <div className="input-group bg_muted rounded flex-grow-1">
                  <div className="input-group-prepend">
                    <span
                      className="input-group-text border-0 bg-transparent text-muted pr-3"
                      id="basic-addon1"
                    >
                      <FaSearch size={16} color="#c2c2c2" />
                    </span>
                  </div>
                  <input
                    type="text"
                    className="form-control border-none bg_muted f-14 text-black outline-none rounded mr-3"
                    placeholder="Search"
                    aria-label="Search"
                    aria-describedby="basic-addon1"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                <button
                  className="btn btn-outline-primary w-116 ml-2 ml-md-3 rounded py-1 mobile-hide"
                  data-toggle="modal"
                  data-target="#filterModal"
                >
                  <FaFilter size={16} />
                  &nbsp;&nbsp; <small>Filter</small>
                </button>
              </div>
            </div>

            {/* Scrollable Table Area */}
            <div
              className="mt-3 hide-scrollbar table-responsive-mobile"
              style={{
                height: "400px",
                overflowY: "auto",
                scrollbarWidth: "none", // Firefox
                msOverflowStyle: "none", // IE and Edge
              }}
            >
              <table className="table table-striped table-bordered mb-0 text-center">
                <thead
                  className="head_light text-white"
                  style={{ position: "sticky", top: 0, zIndex: 10 }}
                >
                  <tr>
                    <th scope="col" style={{ minWidth: "140px" }}>
                      Date Created
                    </th>
                    <th scope="col" style={{ minWidth: "120px" }}>
                      From: Start Date
                    </th>
                    <th scope="col" style={{ minWidth: "120px" }}>
                      To: End Date
                    </th>
                    <th scope="col" style={{ minWidth: "150px" }}>
                      Author
                    </th>
                    <th scope="col" style={{ minWidth: "150px" }}>
                      Position
                    </th>
                    <th scope="col" style={{ minWidth: "180px" }}>
                      Go to Summary with Team Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading && currentPageData.length > 0 ? (
                    currentPageData.map((item, i) => (
                      <tr key={item.id}>
                        <th>
                          <small>
                            {formatUtcTimestamp(item.dateCreated)}
                          </small>
                        </th>
                        <td>
                          <small>
                            {moment(item.fromDate).format("D MMM YYYY")}
                          </small>
                        </td>
                        <td>
                          <small>
                            {moment(item.toDate).format("D MMM YYYY")}
                          </small>
                        </td>
                        <td>
                          <small>{item.author}</small>
                        </td>
                        <td>
                          <small>{item.position}</small>
                        </td>
                        <td>
                          <Link
                            to={{
                              pathname: `/hcp/patient/summary_functions`,
                              state: {
                                fromDate: moment(item.fromDate).format(
                                  "YYYY-MM-DD"
                                ),
                                toDate: moment(item.toDate).format(
                                  "YYYY-MM-DD"
                                ),
                              },
                            }}
                            className="border_success rounded px-4"
                            title="View Team Score Summary"
                          >
                            <i className="fa fa-chevron-right" />
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : !isLoading ? (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        <small className="text-muted">
                          {error
                            ? "Error loading team score summaries. Please try again."
                            : (searchTerm.trim() || filterStartDate || filterEndDate || (filterAuthors && filterAuthors.length > 0))
                            ? "No summaries found matching the current server filters"
                            : "No summaries found"}
                        </small>
                      </td>
                    </tr>
                  ) : null}
                  {isLoading && (
                    <SkeletonTheme baseColor="#e9ecef" highlightColor="#f8f9fa">
                      {[...Array(8)].map((_, index) => (
                        <tr key={`skeleton-${index}`}>
                          <th>
                            <small>
                              <Skeleton width={120} height={12} />
                            </small>
                          </th>
                          <td>
                            <small>
                              <Skeleton width={100} height={12} />
                            </small>
                          </td>
                          <td>
                            <small>
                              <Skeleton width={100} height={12} />
                            </small>
                          </td>
                          <td>
                            <small>
                              <Skeleton width={120} height={12} />
                            </small>
                          </td>
                          <td>
                            <small>
                              <Skeleton width={120} height={12} />
                            </small>
                          </td>
                          <td>
                            <small>
                              <Skeleton width={80} height={12} />
                            </small>
                          </td>
                          <td>
                            <small>
                              <Skeleton width={80} height={12} />
                            </small>
                          </td>
                        </tr>
                      ))}
                    </SkeletonTheme>
                  )}
                </tbody>
              </table>
            </div>

            {/* Fixed Pagination Controls - Outside scrollable area */}
            {!isLoading &&
              summariesData &&
              summariesData.length > 0 &&
              !error && (
                <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap mobile-stack">
                  {/* Page Size Dropdown - Bottom Left */}
                  <div className="d-flex align-items-center mb-2 mb-md-0 mobile-text-center">
                    <span
                      className="text-dark mr-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      Show
                    </span>
                    <select
                      className="border rounded"
                      style={{
                        width: "50px",
                        height: "24px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "#495057",
                        backgroundColor: "#fff",
                        border: "1px solid #ced4da",
                        borderRadius: "6px",
                        padding: "2px 4px",
                        outline: "none",
                      }}
                      value={pageSize}
                      onChange={(e) =>
                        handlePageSizeChange(parseInt(e.target.value))
                      }
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                      <option value={20}>20</option>
                      <option value={25}>25</option>
                    </select>
                    <span
                      className="text-dark ml-2"
                      style={{ fontSize: "12px", fontWeight: "500" }}
                    >
                      entries
                    </span>
                  </div>

                  {/* Pagination Navigation - Bottom Right */}
                  <div className="d-flex align-items-center flex-column flex-md-row">
                    <span
                      className="text-muted mr-0 mr-md-4 mb-2 mb-md-0"
                      style={{ fontSize: "12px" }}
                    >
                      {totalElements > 0
                        ? `Showing ${Math.min(
                            (currentPage - 1) * pageSize + 1,
                            totalElements
                          )} to ${Math.min(
                            currentPage * pageSize,
                            totalElements
                          )} of ${totalElements} entries`
                        : "No entries to show"}
                    </span>

                    {totalPages > 1 && (
                      <nav aria-label="Page navigation">
                        <div className="d-flex align-items-center pagination-mobile">
                          {/* Previous Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center mr-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasPreviousPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasPreviousPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasPreviousPage
                                ? "not-allowed"
                                : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handlePreviousPage}
                            disabled={!hasPreviousPage}
                          >
                            <FaChevronLeft size={12} />
                          </button>

                          {/* Page Numbers - Show fewer on mobile */}
                          <div className="d-flex">
                            {(() => {
                              const pages = Array.from(
                                { length: totalPages },
                                (_, i) => i + 1
                              );

                              // On mobile, show max 5 pages
                              if (window.innerWidth <= 768 && totalPages > 5) {
                                const startPage = Math.max(1, currentPage - 2);
                                const endPage = Math.min(
                                  totalPages,
                                  startPage + 4
                                );
                                return pages
                                  .slice(startPage - 1, endPage)
                                  .map((page) => (
                                    <button
                                      key={page}
                                      className="btn d-flex align-items-center justify-content-center mx-1"
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        border: "1px solid #ced4da",
                                        borderRadius: "6px",
                                        backgroundColor:
                                          page === currentPage
                                            ? "#50a193"
                                            : "#fff",
                                        color:
                                          page === currentPage
                                            ? "#fff"
                                            : "#495057",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                      }}
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  ));
                              }

                              // Desktop - show all pages or use ellipsis for many pages
                              if (totalPages <= 7) {
                                return pages.map((page) => (
                                  <button
                                    key={page}
                                    className="btn d-flex align-items-center justify-content-center mx-1"
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      border: "1px solid #ced4da",
                                      borderRadius: "6px",
                                      backgroundColor:
                                        page === currentPage
                                          ? "#50a193"
                                          : "#fff",
                                      color:
                                        page === currentPage
                                          ? "#fff"
                                          : "#495057",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                ));
                              }

                              // Many pages - show first, current-1, current, current+1, last
                              const result = [];
                              if (currentPage > 3) {
                                result.push(
                                  <button
                                    key={1}
                                    className="btn d-flex align-items-center justify-content-center mx-1"
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      border: "1px solid #ced4da",
                                      borderRadius: "6px",
                                      backgroundColor: "#fff",
                                      color: "#495057",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => handlePageChange(1)}
                                  >
                                    1
                                  </button>
                                );
                                if (currentPage > 4) {
                                  result.push(
                                    <span
                                      key="ellipsis-start"
                                      className="mx-1"
                                      style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                      }}
                                    >
                                      ...
                                    </span>
                                  );
                                }
                              }

                              const startPage = Math.max(1, currentPage - 1);
                              const endPage = Math.min(
                                totalPages,
                                currentPage + 1
                              );

                              for (let i = startPage; i <= endPage; i++) {
                                result.push(
                                  <button
                                    key={i}
                                    className="btn d-flex align-items-center justify-content-center mx-1"
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      border: "1px solid #ced4da",
                                      borderRadius: "6px",
                                      backgroundColor:
                                        i === currentPage ? "#50a193" : "#fff",
                                      color:
                                        i === currentPage ? "#fff" : "#495057",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => handlePageChange(i)}
                                  >
                                    {i}
                                  </button>
                                );
                              }

                              if (currentPage < totalPages - 2) {
                                if (currentPage < totalPages - 3) {
                                  result.push(
                                    <span
                                      key="ellipsis-end"
                                      className="mx-1"
                                      style={{
                                        fontSize: "12px",
                                        color: "#6c757d",
                                      }}
                                    >
                                      ...
                                    </span>
                                  );
                                }
                                result.push(
                                  <button
                                    key={totalPages}
                                    className="btn d-flex align-items-center justify-content-center mx-1"
                                    style={{
                                      width: "24px",
                                      height: "24px",
                                      border: "1px solid #ced4da",
                                      borderRadius: "6px",
                                      backgroundColor: "#fff",
                                      color: "#495057",
                                      cursor: "pointer",
                                      fontSize: "12px",
                                      fontWeight: "500",
                                    }}
                                    onClick={() => handlePageChange(totalPages)}
                                  >
                                    {totalPages}
                                  </button>
                                );
                              }

                              return result;
                            })()}
                          </div>

                          {/* Next Button */}
                          <button
                            className="btn d-flex align-items-center justify-content-center ml-2"
                            style={{
                              width: "24px",
                              height: "24px",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              backgroundColor: !hasNextPage
                                ? "#d7d7d7"
                                : "#fff",
                              color: !hasNextPage
                                ? "rgba(83, 89, 107, 0.5)"
                                : "#495057",
                              cursor: !hasNextPage ? "not-allowed" : "pointer",
                              paddingLeft: "7px",
                              paddingRight: "7px",
                            }}
                            onClick={handleNextPage}
                            disabled={!hasNextPage}
                          >
                            <FaChevronRight size={12} />
                          </button>
                        </div>
                      </nav>
                    )}
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <div
        className="modal fade"
        id="filterModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="filterModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content pt-3">
            <div className="modal-header border-0">
              <div className="flex-grow-1 text-center">
                <h5 className="text_primary" style={{ fontWeight: 600 }}>
                  Search Filter
                </h5>
              </div>
              <button
                type="button"
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span
                  className="text_primary"
                  style={{ fontSize: 40, fontWeight: "600" }}
                  aria-hidden="true"
                >
                  &times;
                </span>
              </button>
            </div>
            <div className="modal-body mb-3 p-0">
              <div className="row mb-3 mx-1">
                <div className="col-md-6">
                  <small className="text-muted">Start Date</small>
                  <PickDate
                    date={startDate}
                    setDate={setStartDate}
                    required={false}
                  />
                </div>
                <div className="col-md-6">
                  <small className="text-muted">End Date</small>
                  <PickDate
                    date={endDate}
                    setDate={setEndDate}
                    required={false}
                  />
                </div>
              </div>

              <Accordion allowZeroExpanded allowMultipleExpanded>
                <AccordionItem uuid="authors-filter">
                  <div className="d-flex justify-content-between align-items-center pr-3">
                    <AccordionItemHeading className="accordion_transparent">
                      <AccordionItemButton>
                        <small className="text-muted">Author</small>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <Switch
                      checked={
                        checkedAuthors.attr == "authors" &&
                        checkedAuthors.check
                      }
                      onChange={handleChecked}
                      onColor="#C7E6F5"
                      onHandleColor="#50A193"
                      handleDiameter={25}
                      uncheckedIcon={false}
                      checkedIcon={false}
                      boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                      height={16}
                      width={48}
                      className="react-switch"
                      id="authors"
                    />
                  </div>
                  <AccordionItemPanel className="bg_lighter_primary m-0 py-2 px-4">
                    <small className="text-muted f-13">
                      Choose as many as applicable:
                    </small>
                    <div className="mt-2">
                      {availableAuthors.map((author, i) => (
                        <div
                          className="dropdown-item noFilterHover form-group form-check ml-4 wa mb-1"
                          key={i}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input mt-2"
                            id={`authorCheck${i}`}
                            checked={selectedAuthors.includes(author.id)}
                            onChange={(e) =>
                              handleAuthorSelection(author.id, e.target.checked)
                            }
                            disabled={!checkedAuthors.check}
                          />
                          <label
                            className="form-check-label f-13"
                            htmlFor={`authorCheck${i}`}
                          >
                            <small>{author.name}</small>
                          </label>
                        </div>
                      ))}
                      {availableAuthors.length === 0 && (
                        <div className="ml-4">
                          <small className="text-muted">
                            No authors found in current data
                          </small>
                        </div>
                      )}
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>
              </Accordion>
            </div>
            <div className="modal-footer d-flex justify-content-between align-items-center border-0">
              <button
                type="button"
                className="btn btn_outline_danger px-5"
                onClick={handleClearFilters}
                data-dismiss="modal"
              >
                CLEAR
              </button>
              <button
                type="button"
                className="btn btn-outline-primary px-5"
                onClick={handleSearch}
                data-dismiss="modal"
              >
                SEARCH
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default FunctionalSummariesTeamScore;
